"use client";

interface TrustedTypes {
  createPolicy(
    policyName: string,
    policyOptions: {
      createHTML?: (input: string) => string;
      createScriptURL?: (input: string) => string;
      createScript?: (input: string) => string;
    }
  ): TrustedTypePolicy;
  getPolicy(policyName: string): TrustedTypePolicy | undefined;
}

interface TrustedTypePolicy {
  createHTML(input: string): string;
  createScriptURL(input: string): string;
  createScript(input: string): string;
}

declare global {
  interface Window {
    trustedTypes: TrustedTypes | undefined;
  }
}

export function initTrustedTypes(): void {
  if (typeof window === "undefined" || typeof window.trustedTypes === "undefined") {
    return;
  }

  if (window.trustedTypes.getPolicy("dompurify")) {
    return;
  }

  // DOMPurify (~125KB) is loaded lazily so it stays out of the initial page
  // bundle; the policy is registered as soon as the module arrives.
  void import("isomorphic-dompurify")
    .then(({ default: DOMPurify }) => {
      if (window.trustedTypes?.getPolicy("dompurify")) {
        return;
      }
      window.trustedTypes?.createPolicy("dompurify", {
        createHTML: (input: string): string => {
          return DOMPurify.sanitize(input, { RETURN_DOM_FRAGMENT: false });
        },
        createScriptURL: (input: string): string => {
          return DOMPurify.sanitize(input, { ALLOW_UNKNOWN_PROTOCOLS: true });
        },
        createScript: (input: string): string => {
          return DOMPurify.sanitize(input);
        },
      });
    })
    .catch(() => {
      // Report-only Trusted Types CSP: sinks are never blocked, so a failed
      // policy load degrades to the browser default instead of breaking the page.
    });
}

if (typeof window !== "undefined") {
  initTrustedTypes();
}